/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, ElementRef, inject, OnDestroy } from '@angular/core';

import { NV_HIG_CONFIG } from '../hig.config';
import { ScrollPlayer } from '../scrolling/scroll-player';
import { ScrollCoordinates } from '../scrolling/scrolling.types';
import { SpatialNavigationDirection } from '../services/spatial-navigation/spatial-navigation-polyfill';
import { ContinuousNavigationSession } from './continuous-navigation-session';

// Six steps per second was empirically tuned on SHIELD to feel responsive without becoming aggressive enough to outrun layout loading.
const ContinuousNavigationCadenceMs = 1000 / 6;

interface ContinuousNavigationRequest {
    readonly direction: SpatialNavigationDirection;
    readonly scroller: HTMLElement;
    readonly destination?: ScrollCoordinates;
    readonly canContinue: () => boolean;
}

@Directive({
    selector: '[nvContinuousNavigationMotion]',
    standalone: true
})
export class ContinuousNavigationMotionDirective implements OnDestroy {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly higConfig = inject(NV_HIG_CONFIG, { optional: true });
    private readonly session = new ContinuousNavigationSession();

    get enabled(): boolean {
        return !!this.higConfig?.tvMode && !!this.higConfig?.spatialNavigationConfig?.continuousNavigationMotionEnabled;
    }

    start(request: ContinuousNavigationRequest): void {
        const destination = request.destination;
        this.session.start({
            direction: request.direction,
            cadenceMs: ContinuousNavigationCadenceMs,
            createPlayer: destination
                ? profile =>
                      new ScrollPlayer(request.scroller, destination.x, destination.y, {
                          duration: profile.durationMs,
                          easingFunction: profile.easingFunction
                      })
                : undefined,
            settle: destination
                ? () => {
                      request.scroller.scrollLeft = destination.x;
                      request.scroller.scrollTop = destination.y;
                  }
                : undefined,
            canContinue: () => this.enabled && request.canContinue(),
            ownsFocus: () => this.elementRef.nativeElement.contains(document.activeElement)
        });
    }

    cancel(): void {
        this.session.cancel();
    }

    ngOnDestroy(): void {
        this.session.destroy();
    }
}
